// Heuristic, not cryptographically rigorous: estimates search-space
// entropy and trims it for the cheap-to-detect patterns real attackers
// exploit first (repeats, sequences, embedded common passwords, embedded
// years). Server-side policy stays length-only — this exists to nudge
// users toward longer, less guessable passwords, not to gate.

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const SCORE_PRESENTATION = [
  { filled: 1, color: "bg-red-400", label: "Very weak" },
  { filled: 2, color: "bg-orange-400", label: "Weak" },
  { filled: 3, color: "bg-amber-400", label: "Okay" },
  { filled: 3, color: "bg-emerald-400", label: "Good" },
  { filled: 4, color: "bg-emerald-500", label: "Strong" },
];

const EMPTY_COLOR = "bg-ink-100";
const ALL_COLORS = [EMPTY_COLOR, ...SCORE_PRESENTATION.map((p) => p.color)];

const EMPTY_PRESENTATION = { filled: 0, color: EMPTY_COLOR };

// Top common-password fragments. Lowercase substring match, so "Password1!"
// and "letmein2024" both get caught. Kept short on purpose — covers the
// universally-leaked fragments without dragging in a dictionary.
const COMMON_FRAGMENTS = [
  "password",
  "passw0rd",
  "qwerty",
  "asdf",
  "zxcv",
  "letmein",
  "welcome",
  "admin",
  "login",
  "iloveyou",
  "monkey",
  "dragon",
  "master",
  "trustno1",
  "abc123",
  "ninja",
  "azerty",
  "shadow",
  "superman",
  "batman",
  "starwars",
  "liferida",
];

// Bit thresholds that map raw entropy estimates onto the 0-4 score range.
// Calibrated so a 12-char password using two character classes lands
// around 2 ("Okay") and 16+ chars across three classes lands at 4.
const SCORE_THRESHOLDS = [28, 40, 56, 72];

function charsetSize(password) {
  let size = 0;
  let hasLower = false;
  let hasUpper = false;
  let hasDigit = false;
  let hasSymbol = false;
  let hasOther = false;
  for (const ch of password) {
    if (ch >= "a" && ch <= "z") hasLower = true;
    else if (ch >= "A" && ch <= "Z") hasUpper = true;
    else if (ch >= "0" && ch <= "9") hasDigit = true;
    else if (ch.charCodeAt(0) < 128) hasSymbol = true;
    else hasOther = true;
  }
  if (hasLower) size += 26;
  if (hasUpper) size += 26;
  if (hasDigit) size += 10;
  if (hasSymbol) size += 33;
  if (hasOther) size += 100;

  return size || 1;
}

function longestRepeatRun(password) {
  let max = 1;
  let run = 1;
  for (let i = 1; i < password.length; i++) {
    run = password[i] === password[i - 1] ? run + 1 : 1;
    if (run > max) max = run;
  }

  return max;
}

function longestSequenceRun(password) {
  let max = 1;
  let run = 1;
  for (let i = 1; i < password.length; i++) {
    const delta = password.charCodeAt(i) - password.charCodeAt(i - 1);
    run = delta === 1 || delta === -1 ? run + 1 : 1;
    if (run > max) max = run;
  }

  return max;
}

function containsCommonFragment(lower) {
  for (const frag of COMMON_FRAGMENTS) {
    if (lower.includes(frag)) return frag;
  }

  return null;
}

function containsYear(password) {
  // Years 1900-2099 — common padding pattern.
  return /(?:19|20)\d{2}/.test(password);
}

export function scorePassword(password) {
  if (!password) {
    return { score: 0, bits: 0, hint: null, presentation: null };
  }

  const length = password.length;
  const charset = charsetSize(password);
  let bits = length * Math.log2(charset);

  const repeatRun = longestRepeatRun(password);
  if (repeatRun >= 3) {
    bits -= (repeatRun - 2) * Math.log2(charset) * 0.8;
  }

  const seqRun = longestSequenceRun(password);
  if (seqRun >= 4) {
    bits -= (seqRun - 3) * Math.log2(charset) * 0.7;
  }

  const lower = password.toLowerCase();
  const commonHit = containsCommonFragment(lower);
  if (commonHit) {
    bits -= Math.min(bits, commonHit.length * 4);
  }

  if (containsYear(password)) {
    // 4-digit year ≈ log2(200) ≈ 7.6 bits of real entropy, not log2(10000).
    bits -= 4 * Math.log2(10) - Math.log2(200);
  }

  if (length < 12) {
    // Under-policy: even an exotic short string isn't strong.
    bits = Math.min(bits, 24);
  }

  bits = Math.max(0, bits);

  let score = 0;
  while (score < SCORE_THRESHOLDS.length && bits >= SCORE_THRESHOLDS[score]) {
    score++;
  }

  const presentation = SCORE_PRESENTATION[score];

  let hint = presentation.label;
  if (length < 12) {
    hint = `Add ${12 - length} more character${12 - length === 1 ? "" : "s"}.`;
  } else if (commonHit) {
    hint = `Avoid common words like "${commonHit}".`;
  } else if (repeatRun >= 4) {
    hint = "Avoid long runs of the same character.";
  } else if (seqRun >= 4) {
    hint = "Avoid keyboard or alphabet sequences.";
  } else if (score < 2) {
    hint = "Try mixing in upper-case letters, numbers, or symbols.";
  } else if (score < 4) {
    hint = `${presentation.label} — longer is stronger.`;
  }

  return { score, bits, hint, presentation };
}

function paint(container, presentation, hint) {
  const segments = container.querySelectorAll("[data-strength-segment]");
  segments.forEach((segment, idx) => {
    const color = idx < presentation.filled ? presentation.color : EMPTY_COLOR;
    segment.classList.remove(...ALL_COLORS);
    segment.classList.add(color);
  });

  const label = container.querySelector("[data-strength-label]");
  if (label) {
    label.textContent = hint;
  }
}

export function initPasswordStrength(root = document) {
  const containers = root.querySelectorAll("[data-password-strength]");

  containers.forEach((container) => {
    if (!firstBind(container)) return;

    const inputId = container.dataset.passwordStrength;
    const input = inputId
      ? document.getElementById(inputId)
      : container.querySelector("input[type=password]");
    if (!input) return;

    const defaultHint =
      container.querySelector("[data-strength-label]")?.textContent ?? "";

    input.addEventListener("input", () => {
      if (!input.value) {
        paint(container, EMPTY_PRESENTATION, defaultHint);

        return;
      }

      const { presentation, hint } = scorePassword(input.value);
      paint(container, presentation, hint);
    });
  });
}
